import React, { createContext, useEffect, useState } from 'react';
import { Box, Button, Paper, Stack } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import Toast from '../../components/Toast';
import { options } from '../../helpers/toastOptions';
import { parsePhone } from '../../helpers/parseValues';
import { Header } from '../../components/Header';
import { ModalLoading } from '../../components/ModalLoading';
import { ModalFinancialRestriction } from './components/ModalFinancialRestriction';
import { useAuth } from '../../hooks/Auth';
import { useIntegration } from '../../hooks/Integration';
import { useManager } from '../../hooks/Manager';
import { useModal } from '../../hooks/Modal';
import { usePickup } from '../../hooks/Pickup';
import { useWorkflow } from '../../hooks/Workflow';
import { Container, Content, Route, CollectTypeTitle } from './styled';

export const CollectTypeContext = createContext({});

const CollectType = () => {
  const { user, userAddressList, signInInternal } = useAuth();
  const { verifyUserRestriction } = useIntegration();
  const {
    getPickupManagerParameterization,
    getPickupManagerBranchValidations,
    getPickupManagerBranchGeneralConfigAccess
  } = useManager();
  const { openModal, closeModal, openedModal } = useModal();
  const {
    setBranchParameterizationId,
    setBranchWorkflowParameterizationId,
    setEnablePreCollection,
    setAcceptDeclaration,
    setEnableCheckList,
    setEnableDisclaimerForDamages,
    cancelDefaultPickup
  } = usePickup();
  const {
    getWorkflowManagerParameterization,
    getWorkflowManagerGeneralWorkflowTypes
  } = useWorkflow();

  const maxSM = useMediaQuery('(max-width:768px)');
  const maxMD = useMediaQuery('(max-width:900px)');

  const [userHasRestriction, setUserHasRestriction] = useState(true);
  const [validateIncompatibleGoods, setValidateIncompatibleGoods] =
    useState(true);
  // const [disablePickupRequest, setDisablePickupRequest] = useState(false);
  const [protocolCollectCanceled, setProtocolCollectCanceled] = useState('');

  const history = useHistory();

  const handleClickDefaultCollectButton = () => {
    const { roles } = user;

    if (roles) {
      const roleList = JSON.parse(roles);

      if (
        roleList?.length <= 0 ||
        !roleList?.find(({ role }) => role === 'PED_COLETA_PADRAO')
      ) {
        toast(
          <Toast title="Atenção" type="error">
            Ainda não tem permissão para solicitar uma Coleta Padrão?
            <br />
            Contate o departamento comercial de atendimento da sua localidade.
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href={`${process.env.REACT_APP_URL_WEB}/area-do-cliente/minha-conta/`}
            >
              Clique aqui
            </a>
            , que vamos direcioná-lo para a consulta!
          </Toast>,
          options
        );
        return;
      }
    }

    localStorage.setItem('@Braspress:collectType', 'defaultCollect');

    if (validateIncompatibleGoods) {
      history.push('/acesso-rapido/pedido-de-coleta-perfil');
    } else {
      history.push('/acesso-rapido/tipo-de-solicitante');
    }
  };

  const handleClickReverseCollectButton = () => {
    const { roles } = user;

    if (roles) {
      const roleList = JSON.parse(roles);

      if (
        roleList?.length <= 0 ||
        !roleList?.find(({ role }) => role === 'PED_COLETA_REVERSA')
      ) {
        toast(
          <Toast title="Atenção" type="error">
            Ainda não tem permissão para solicitar uma Coleta Reversa?
            <br />
            Contate o departamento comercial de atendimento da sua localidade.
            <br />
            <a
              target="_blank"
              rel="noreferrer"
              href={`${process.env.REACT_APP_URL_WEB}/area-do-cliente/minha-conta/`}
            >
              Clique aqui
            </a>
            , que vamos direcioná-lo para a consulta!
          </Toast>,
          options
        );

        return;
      }
    }
    // console.log('ROLAQ', roles);

    localStorage.setItem('@Braspress:collectType', 'reverseCollect');

    if (validateIncompatibleGoods) {
      history.push('/acesso-rapido/pedido-de-coleta-perfil');
    } else {
      history.push('/acesso-rapido/pedido-de-coleta/reversa');
    }
  };

  useEffect(async () => {
    const { search } = window.location;
    const legacyToken = new URLSearchParams(search).get('token');
    const localLegacyToken = localStorage.getItem('@Braspress:legacyToken');

    if (!user || (legacyToken && legacyToken !== localLegacyToken)) {
      openModal('modalLoading');

      if (legacyToken) {
        const signInResponse = await signInInternal({ legacyToken });

        if (signInResponse === 'Invalid token') {
          window.location.href = `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
        }
      } else {
        window.location.href = `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
      }

      return;
    }

    openModal('modalLoading');

    try {
      const pickupManagerResponse = await getPickupManagerParameterization({
        branchId: 1,
        transportTypeId: 9,
        pickupTypeId: 1,
        inputTypeId: 5
      });

      const workflowManagerResponse = await getWorkflowManagerParameterization({
        branchId: 1,
        transportTypeId: 9,
        pickupTypeId: 1,
        inputTypeId: 5
      });

      if (workflowManagerResponse?.length > 0) {
        setBranchWorkflowParameterizationId(
          workflowManagerResponse[0]?.branchParameterizationId
        );

        const workflowTypesResponse =
          await getWorkflowManagerGeneralWorkflowTypes({
            branchWorkflowParameterizationId:
              workflowManagerResponse[0]?.branchParameterizationId
          });

        if (workflowTypesResponse?.status === 200) {
          const { data: workflowTypes } = workflowTypesResponse?.data;

          if (workflowTypes?.length > 0) {
            const preCollection = workflowTypes.find(
              (value) => value.description === 'Pré-coleta'
            );

            if (preCollection) {
              preCollection?.enabled === true
                ? setEnablePreCollection(true)
                : setEnablePreCollection(false);
            }
          }
        }
      }

      if (pickupManagerResponse?.length > 0) {
        setBranchParameterizationId(
          pickupManagerResponse[0]?.branchParameterizationId
        );

        // const generalConfigAccessResponse =
        //   await getPickupManagerBranchGeneralConfigAccess({
        //     branchParameterizationId:
        //       pickupManagerResponse[0]?.branchParameterizationId
        //   });

        // if (generalConfigAccessResponse?.status === 200) {
        //   if (
        //     generalConfigAccessResponse?.data?.enablePickupRequest === false
        //   ) {
        //     setDisablePickupRequest(true);
        //     closeModal();
        //     return;
        //   }
        // }
      }

      const validationResponse = await getPickupManagerBranchValidations({
        branchParameterizationId:
          pickupManagerResponse[0]?.branchParameterizationId
      });

      let validateRestriction = true;

      if (validationResponse?.length > 0) {
        if (
          !validationResponse[0]?.validatePendingCharge &&
          !validationResponse[0]?.validateLegalPending
        ) {
          validateRestriction = false;
        }

        if (!validationResponse[0]?.validateIncompatibleMerchandise) {
          setValidateIncompatibleGoods(false);
        }

        if (!validationResponse[0]?.enableMerchandiseDeclarationTypeRequest) {
          setAcceptDeclaration(false);
        } else {
          setAcceptDeclaration(true);
        }

        if (!validationResponse[0]?.enableGeneralChecklist) {
          setEnableCheckList(false);
        } else {
          setEnableCheckList(true);
        }

        if (!validationResponse[0]?.enableDamageWaiverTerm) {
          setEnableDisclaimerForDamages(false);
        } else {
          setEnableDisclaimerForDamages(true);
        }
      }

      if (validateRestriction) {
        const response = await verifyUserRestriction({
          document: user.document
        });

        if (response.isBlocked) {
          setUserHasRestriction(true);
          const address = {
            zipPostalCode: null,
            streetAddress: null,
            streetAddressNumber: null,
            additionalInformation: null,
            suburb: null,
            city: null,
            state: null,
            country: null
          };

          if (userAddressList.length > 0) {
            address.zipPostalCode = parsePhone(
              userAddressList[0].zipPostalCode
            );
            address.streetAddress = userAddressList[0].streetAddress;
            address.streetAddressNumber =
              userAddressList[0].streetAddressNumber;
            address.additionalInformation =
              userAddressList[0].additionalInformation;
            address.suburb = userAddressList[0].suburb;
            address.city = userAddressList[0].city;
            address.state = userAddressList[0].state;
            address.country = userAddressList[0].country;
          }

          const cancelResponse = await cancelDefaultPickup({
            data: {
              pickup: {
                requesterCorporation: {
                  requesterType: 'sender',
                  document: String(user.document),
                  name: user.name,
                  address,
                  contactList: []
                },
                shipmentList: [
                  {
                    transportTypeId: null,
                    freightTypeId: null,
                    senderCorporation: {
                      document: String(user.document),
                      name: user.name,
                      address,
                      contactList: []
                    },
                    receiverCorporation: null,
                    shipmentAvailability: null,
                    shipmentPackageList: null,
                    taxInformation: null
                  }
                ],
                pickupCancelType: 'Restrição Financeira ou Jurídica'
              }
            }
          });

          if (cancelResponse.status === 200) {
            setProtocolCollectCanceled(cancelResponse.data.data.protocol);
          }

          openModal('modalFinancialRestriction');
        } else {
          setUserHasRestriction(false);

          closeModal();
        }
      } else {
        setUserHasRestriction(false);

        closeModal();
      }
    } catch (error) {
      toast(
        <Toast
          title="Atenção!"
          content="Falha ao verificar permissões. Recarregue a página ou tente novamente mais tarde."
          type="error"
        />,
        options
      );
      // eslint-disable-next-line no-console
      console.log(error);

      closeModal();
    }
  }, [user]);

  const contextValues = { protocolCollectCanceled };

  return (
    <CollectTypeContext.Provider value={contextValues}>
      <Container>
        {/* <Header /> */}
        {user && (
          <Content>
            <Box margin="0 40px" paddingBottom="40px">
              {/* <Route>
                {!maxSM && (
                  <>
                    Home {'>'} Acesso Rápido {'>'} Pedido de Coleta
                  </>
                )}

                {!!user && (
                  <Stack
                    direction="row"
                    spacing={2}
                    style={{
                      alignItems: 'center',
                      marginLeft: maxSM && 'auto'
                    }}
                  >
                    <p>{user.name}</p>
                    <Button
                      type="button"
                      variant="contained"
                      color="secondary"
                      data-testid="signOutButton"
                      onClick={() => {
                        signOut();

                        window.location.href =
                          `${process.env.REACT_APP_URL_CLIENT_VIEW}${process.env.REACT_APP_URL_BOTAO_VOLTAR}`;
                      }}
                      size="small"
                    >
                      Sair
                    </Button>
                  </Stack>
                )}
              </Route> */}

              <Box
                style={{
                  marginTop: 128,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <Paper elevation={3} style={{ padding: '24px 40px' }}>
                  <CollectTypeTitle>
                    Selecione o tipo de coleta
                    {/* <hr /> */}
                  </CollectTypeTitle>

                  <Stack
                    direction="row"
                    justifyContent="center"
                    gap={8}
                    marginTop={8}
                  >
                    <Button
                      type="button"
                      variant="contained"
                      data-testid="defaultPickupButton"
                      onClick={handleClickDefaultCollectButton}
                      disabled={userHasRestriction}
                    >
                      Padrão
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      data-testid="reversePickupButton"
                      onClick={handleClickReverseCollectButton}
                      disabled={
                        userHasRestriction || !user?.isReversePickupAllowed
                      }
                    >
                      Reversa
                    </Button>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          </Content>
        )}
      </Container>

      {openedModal === 'modalLoading' && <ModalLoading />}
      {openedModal === 'modalFinancialRestriction' && (
        <ModalFinancialRestriction />
      )}
    </CollectTypeContext.Provider>
  );
};

export default CollectType;
